import { useRef, useState } from "react";

import { DatePicker } from "@/components/dates/DatePicker";
import { DateRangePicker } from "@/components/dates/DateRangePicker";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { showDay } from "@/lib/calendar-dates";
import {
  QUARTER_HOURS,
  joinDayAndTime,
  parseTypedTime,
  showTime,
  splitDayAndTime,
  type DayAndTime,
} from "@/lib/store-time";
import { cn } from "@/lib/utils";

/**
 * A time of day, or the whole day: typed, or picked from the quarter hours.
 *
 * Typed because a change log is asked about to the minute — "what happened at
 * 3:31" — and a list of every minute is not a list anyone scrolls. The list
 * is there for the round times most filters want. Empty is the whole day.
 */
function TimeField({
  label,
  time,
  disabled,
  onChange,
}: {
  label: string;
  time: string;
  disabled: boolean;
  onChange: (time: string) => void;
}) {
  const shown = time === "" ? "" : showTime(time);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(shown);
  // The time the text was last in step with: one picked from the list
  // replaces whatever was half-typed.
  const [seen, setSeen] = useState(time);
  const [invalid, setInvalid] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  if (seen !== time) {
    setSeen(time);
    setText(shown);
    setInvalid(false);
  }

  function commit() {
    if (text.trim() === "") {
      setInvalid(false);
      if (time !== "") onChange("");
      return;
    }
    const typed = parseTypedTime(text);
    if (typed === undefined) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (typed === time) setText(shown);
    else onChange(typed);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          ref={input}
          aria-label={label}
          aria-invalid={invalid}
          data-slot="time-field"
          placeholder="Whole day"
          disabled={disabled}
          className="h-8 min-w-0 flex-1"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit();
              setOpen(false);
            }
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="max-h-56 w-36 overflow-y-auto p-1"
        // The field keeps the focus, so typing carries on with the list open.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (
            event.target instanceof Node &&
            input.current?.contains(event.target)
          ) {
            event.preventDefault();
          }
        }}
      >
        {["", ...QUARTER_HOURS].map((option) => (
          <button
            key={option}
            type="button"
            className={cn(
              "hover:bg-accent flex w-full rounded-sm px-2 py-1 text-left text-sm tabular-nums",
              option === time && "bg-accent font-medium",
            )}
            onClick={() => {
              onChange(option);
              setOpen(false);
            }}
          >
            {option === "" ? "Whole day" : showTime(option)}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function show({ day, time }: DayAndTime): string {
  return time === "" ? showDay(day) : `${showDay(day)}, ${showTime(time)}`;
}

const STORE_CLOCK_NOTE = (
  <p className="text-muted-foreground px-1 pt-1 text-xs">
    Times are the store’s (IST).
  </p>
);

/**
 * One day, and optionally a time on it (#555) — the value side of a
 * condition on a datetime field.
 *
 * With no time it is the whole day, exactly what the same condition on a
 * date field means, so "placed on 30 Sep" does not need a time to be
 * invented for it.
 */
export function DateTimePicker({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  const parts = splitDayAndTime(value);
  return (
    <DatePicker
      className={className}
      value={parts.day}
      display={show(parts)}
      keepOpen
      onChange={(day) => {
        onChange(day === "" ? "" : joinDayAndTime({ day, time: parts.time }));
      }}
      footer={
        <div className="border-t pt-2">
          <TimeField
            label="Time"
            time={parts.time}
            disabled={parts.day === ""}
            onChange={(time) => {
              onChange(joinDayAndTime({ day: parts.day, time }));
            }}
          />
          {STORE_CLOCK_NOTE}
        </div>
      }
    />
  );
}

/**
 * A start and an end, each a day and optionally a time on it.
 *
 * An end with no time is the whole of its day, so a range of two bare days
 * is those days in full, as it is for a date field.
 */
export function DateTimeRangePicker({
  from,
  to,
  onChange,
  className,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  className?: string;
}) {
  const start = splitDayAndTime(from);
  const end = splitDayAndTime(to);
  return (
    <DateRangePicker
      className={className}
      from={start.day}
      to={end.day}
      display={
        start.day && end.day
          ? start.time === "" && end.time === "" && start.day === end.day
            ? showDay(start.day)
            : `${show(start)} – ${show(end)}`
          : undefined
      }
      keepOpen
      onChange={(range) => {
        onChange({
          from: joinDayAndTime({ day: range.from, time: start.time }),
          to: joinDayAndTime({ day: range.to, time: end.time }),
        });
      }}
      footer={
        <div className="border-t pt-2">
          <div className="flex items-center gap-2">
            <TimeField
              label="Start time"
              time={start.time}
              disabled={start.day === ""}
              onChange={(time) => {
                onChange({
                  from: joinDayAndTime({ day: start.day, time }),
                  to,
                });
              }}
            />
            <span className="text-muted-foreground text-xs">to</span>
            <TimeField
              label="End time"
              time={end.time}
              disabled={end.day === ""}
              onChange={(time) => {
                onChange({ from, to: joinDayAndTime({ day: end.day, time }) });
              }}
            />
          </div>
          {STORE_CLOCK_NOTE}
        </div>
      }
    />
  );
}
